import React, { useState } from "react";
import MainLayout from "../components/layout/MainLayout";
import CampusList from "../features/campuses/CampusList";
import CampusForm from "../features/campuses/CampusForm";
import { useNotification } from "../context/NotificationContext";
import campusService from "../services/campusService";

const Campuses = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingCampus, setEditingCampus] = useState(null);
  const [viewingCampus, setViewingCampus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const { showSuccess, showError } = useNotification();

  const handleCreate = () => {
    setEditingCampus(null);
    setShowForm(true);
  };

  const handleEdit = (campus) => {
    setEditingCampus(campus);
    setShowForm(true);
  };

  const handleView = (campus) => {
    setViewingCampus(campus);
  };

  const handleCloseView = () => {
    setViewingCampus(null);
  };

  const handleFormSubmit = async (formData) => {
    try {
      setLoading(true);
      let response;

      if (editingCampus) {
        response = await campusService.updateCampus(editingCampus.id, formData);
        if (response.success) {
          showSuccess("Campus updated successfully!");
        }
      } else {
        response = await campusService.createCampus(formData);
        if (response.success) {
          showSuccess("Campus created successfully!");
        }
      }

      setShowForm(false);
      setEditingCampus(null);
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      showError("Campus creation failed!");
    } finally {
      setLoading(false);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingCampus(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <MainLayout>
      <div className="campuses-page">
        {/* Page Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="h3 mb-1 fw-bold text-dark">Campus Management</h1>
            <p className="text-muted mb-0">
              Manage university campuses and their locations
            </p>
          </div>
          <button
            className="btn btn-primary"
            onClick={handleCreate}
          >
            <i className="fas fa-plus me-2"></i>
            Add Campus
          </button>
        </div>

        {/* Form Modal - Bootstrap native for reliability */}
        {showForm && (
          <div
            className="modal show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
            tabIndex={-1}
          >
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i
                      className={`fas ${editingCampus ? "fa-edit" : "fa-plus"} me-2`}
                    ></i>
                    {editingCampus ? "Edit Campus" : "Add New Campus"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleFormCancel}
                    disabled={loading}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <CampusForm
                    campus={editingCampus}
                    onSubmit={handleFormSubmit}
                    onCancel={handleFormCancel}
                    loading={loading}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* View Modal - Bootstrap native */}
        {viewingCampus && (
          <div
            className="modal show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
            tabIndex={-1}
          >
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="fas fa-building me-2"></i>
                    Campus Details
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseView}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-8">
                      {/* Campus Header */}
                      <div className="d-flex align-items-center mb-4">
                        <div
                          className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-3"
                          style={{ width: "60px", height: "60px" }}
                        >
                          <i className="fas fa-building text-white fs-4"></i>
                        </div>
                        <div>
                          <h4 className="fw-bold text-dark mb-0">
                            {viewingCampus.nameEn}
                          </h4>
                          <p className="text-muted mb-1">
                            {viewingCampus.nameLocal}
                          </p>
                          <span className="badge bg-primary">
                            <i className="fas fa-university me-1"></i>
                            {viewingCampus.universityName}
                          </span>
                        </div>
                      </div>

                      {/* Basic Information */}
                      <div className="row mb-4">
                        <div className="col-md-6">
                          <label className="form-label fw-bold text-muted small">
                            CAMPUS ID
                          </label>
                          <p className="text-dark fs-5">{viewingCampus.id}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold text-muted small">
                            UNIVERSITY ID
                          </label>
                          <p className="text-dark fs-5">
                            {viewingCampus.universityId}
                          </p>
                        </div>
                      </div>

                      {/* Names */}
                      <div className="row mb-4">
                        <div className="col-md-6">
                          <label className="form-label fw-bold text-muted small">
                            ENGLISH NAME
                          </label>
                          <p className="text-dark">{viewingCampus.nameEn}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold text-muted small">
                            LOCAL NAME
                          </label>
                          <p className="text-dark">{viewingCampus.nameLocal}</p>
                        </div>
                      </div>

                      {/* Location */}
                      <div className="row mb-4">
                        <div className="col-md-6">
                          <label className="form-label fw-bold text-muted small">
                            CITY
                          </label>
                          <p className="text-dark">
                            {viewingCampus.city || "Not specified"}
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold text-muted small">
                            COUNTRY
                          </label>
                          <p className="text-dark">
                            {viewingCampus.country || "Not specified"}
                          </p>
                        </div>
                      </div>

                      {/* Timestamps */}
                      <div className="row">
                        <div className="col-md-6">
                          <label className="form-label fw-bold text-muted small">
                            CREATED
                          </label>
                          <p className="text-dark">
                            {formatDate(viewingCampus.createdAt)}
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold text-muted small">
                            LAST UPDATED
                          </label>
                          <p className="text-dark">
                            {formatDate(viewingCampus.updatedAt)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Statistics Panel */}
                    <div className="col-md-4">
                      <div className="card border-0 bg-light h-100">
                        <div className="card-body">
                          <h6 className="fw-bold mb-4">Campus Statistics</h6>

                          <div className="d-flex align-items-center mb-4">
                            <div
                              className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-3"
                              style={{ width: "50px", height: "50px" }}
                            >
                              <i className="fas fa-user-graduate text-white fs-4"></i>
                            </div>
                            <div>
                              <div className="fw-bold fs-3">
                                {viewingCampus.studentCount || 0}
                              </div>
                              <small className="text-muted">Students</small>
                            </div>
                          </div>

                          <div className="d-flex align-items-center mb-4">
                            <div
                              className="bg-success rounded-circle d-flex align-items-center justify-content-center me-3"
                              style={{ width: "50px", height: "50px" }}
                            >
                              <i className="fas fa-users text-white fs-4"></i>
                            </div>
                            <div>
                              <div className="fw-bold fs-3">
                                {viewingCampus.clubCount || 0}
                              </div>
                              <small className="text-muted">Clubs</small>
                            </div>
                          </div>

                          <div className="d-flex align-items-center mb-4">
                            <div
                              className="bg-warning rounded-circle d-flex align-items-center justify-content-center me-3"
                              style={{ width: "50px", height: "50px" }}
                            >
                              <i className="fas fa-calendar-alt text-white fs-4"></i>
                            </div>
                            <div>
                              <div className="fw-bold fs-3">
                                {viewingCampus.eventCount || 0}
                              </div>
                              <small className="text-muted">Events</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => {
                      handleCloseView();
                      handleEdit(viewingCampus);
                    }}
                  >
                    <i className="fas fa-edit me-2"></i>
                    Edit Campus
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseView}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Campuses List */}
        <CampusList
          onEdit={handleEdit}
          onView={handleView}
          refreshTrigger={refreshTrigger}
        />
      </div>
    </MainLayout>
  );
};

export default Campuses;
